// Form options and enums for patient forms
// This file contains all dropdown options used across patient forms

export interface FormOption {
  value: string;
  label: string;
  labelEl?: string; // Greek translation
}

// Helper function to get localized label
export const getLocalizedLabel = (option: FormOption, language: 'en' | 'el'): string => {
  return language === 'el' && option.labelEl ? option.labelEl : option.label;
};

// Family and relationship status options
export const familyStatusOptions: FormOption[] = [
  { value: 'single', label: 'Single', labelEl: 'Άγαμος/η' },
  { value: 'married', label: 'Married', labelEl: 'Παντρεμένος/η' },
  { value: 'divorced', label: 'Divorced', labelEl: 'Διαζευγμένος/η' },
  { value: 'widowed', label: 'Widowed', labelEl: 'Χήρος/α' },
  { value: 'separated', label: 'Separated', labelEl: 'Χωρισμένος/η' }
];

// Education level options
export const educationLevelOptions: FormOption[] = [
  { value: 'primary', label: 'Primary Education', labelEl: 'Δημοτικό' },
  { value: 'secondary', label: 'Secondary Education', labelEl: 'Γυμνάσιο' },
  { value: 'high_school', label: 'High School', labelEl: 'Λύκειο' },
  { value: 'vocational', label: 'Vocational Training', labelEl: 'Επαγγελματική Εκπαίδευση' },
  { value: 'bachelors', label: "Bachelor's Degree", labelEl: 'Πτυχίο' },
  { value: 'masters', label: "Master's Degree", labelEl: 'Μεταπτυχιακό' },
  { value: 'phd', label: 'PhD/Doctorate', labelEl: 'Διδακτορικό' },
  { value: 'other', label: 'Other', labelEl: 'Άλλο' }
];

// Support network options
export const supportNetworkOptions: FormOption[] = [
  { value: 'family', label: 'Family', labelEl: 'Οικογένεια' },
  { value: 'friends', label: 'Friends', labelEl: 'Φίλοι' },
  { value: 'partner', label: 'Partner/Spouse', labelEl: 'Σύντροφος/Σύζυγος' },
  { value: 'professional', label: 'Professional Support', labelEl: 'Επαγγελματική Υποστήριξη' },
  { value: 'community', label: 'Community', labelEl: 'Κοινότητα' },
  { value: 'religious', label: 'Religious Community', labelEl: 'Θρησκευτική Κοινότητα' },
  { value: 'none', label: 'No Support Network', labelEl: 'Χωρίς Δίκτυο Υποστήριξης' },
  { value: 'other', label: 'Other', labelEl: 'Άλλο' }
];

// Ethnicity options - comprehensive list with focus on Greek demographics
export const ethnicityOptions: FormOption[] = [
  // Greek and Balkan
  { value: 'greek', label: 'Greek', labelEl: 'Έλληνας/ίδα' },
  { value: 'albanian', label: 'Albanian', labelEl: 'Αλβανός/ίδα' },
  { value: 'bulgarian', label: 'Bulgarian', labelEl: 'Βούλγαρος/α' },
  { value: 'romanian', label: 'Romanian', labelEl: 'Ρουμάνος/α' },
  { value: 'serbian', label: 'Serbian', labelEl: 'Σέρβος/α' },
  { value: 'north_macedonian', label: 'North Macedonian', labelEl: 'Βορειομακεδόνας/ίσσα' },
  { value: 'turkish', label: 'Turkish', labelEl: 'Τούρκος/α' },
  { value: 'armenian', label: 'Armenian', labelEl: 'Αρμένιος/α' },
  { value: 'roma', label: 'Roma', labelEl: 'Ρομά' },
  { value: 'pomak', label: 'Pomak', labelEl: 'Πομάκος/α' },
  { value: 'vlach', label: 'Vlach/Aromanian', labelEl: 'Βλάχος/α' },
  { value: 'cypriot', label: 'Cypriot', labelEl: 'Κύπριος/α' },
  
  // European
  { value: 'italian', label: 'Italian', labelEl: 'Ιταλός/ίδα' },
  { value: 'german', label: 'German', labelEl: 'Γερμανός/ίδα' },
  { value: 'french', label: 'French', labelEl: 'Γάλλος/ίδα' },
  { value: 'british', label: 'British', labelEl: 'Βρετανός/ίδα' },
  { value: 'spanish', label: 'Spanish', labelEl: 'Ισπανός/ίδα' },
  { value: 'polish', label: 'Polish', labelEl: 'Πολωνός/ίδα' },
  { value: 'russian', label: 'Russian', labelEl: 'Ρώσος/α' },
  { value: 'ukrainian', label: 'Ukrainian', labelEl: 'Ουκρανός/ίδα' },
  { value: 'georgian', label: 'Georgian', labelEl: 'Γεωργιανός/ή' },
  { value: 'moldovan', label: 'Moldovan', labelEl: 'Μολδαβός/ή' },
  
  // Middle Eastern
  { value: 'arab', label: 'Arab', labelEl: 'Άραβας/ισσα' },
  { value: 'syrian', label: 'Syrian', labelEl: 'Σύριος/α' },
  { value: 'iraqi', label: 'Iraqi', labelEl: 'Ιρακινός/ή' },
  { value: 'afghan', label: 'Afghan', labelEl: 'Αφγανός/ή' },
  { value: 'pakistani', label: 'Pakistani', labelEl: 'Πακιστανός/ή' },
  { value: 'iranian', label: 'Iranian', labelEl: 'Ιρανός/ή' },
  { value: 'kurdish', label: 'Kurdish', labelEl: 'Κούρδος/α' },
  
  // African
  { value: 'african', label: 'African', labelEl: 'Αφρικανός/ή' },
  { value: 'nigerian', label: 'Nigerian', labelEl: 'Νιγηριανός/ή' },
  { value: 'ethiopian', label: 'Ethiopian', labelEl: 'Αιθίοπας/ισσα' },
  { value: 'somali', label: 'Somali', labelEl: 'Σομαλός/ή' },
  
  // Asian
  { value: 'chinese', label: 'Chinese', labelEl: 'Κινέζος/ισσα' },
  { value: 'indian', label: 'Indian', labelEl: 'Ινδός/ή' },
  { value: 'filipino', label: 'Filipino', labelEl: 'Φιλιππινέζος/ισσα' },
  { value: 'bangladeshi', label: 'Bangladeshi', labelEl: 'Μπαγκλαντεσιανός/ή' },
  
  // Special categories
  { value: 'mixed', label: 'Mixed/Multiethnic', labelEl: 'Μικτής Καταγωγής' },
  { value: 'other', label: 'Other', labelEl: 'Άλλο' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say', labelEl: 'Προτιμώ να μην απαντήσω' }
];

// Financial status options
export const financialStatusOptions: FormOption[] = [
  { value: 'excellent', label: 'Excellent', labelEl: 'Εξαιρετική' },
  { value: 'good', label: 'Good', labelEl: 'Καλή' },
  { value: 'stable', label: 'Stable', labelEl: 'Σταθερή' },
  { value: 'difficult', label: 'Difficult', labelEl: 'Δύσκολη' },
  { value: 'poor', label: 'Poor', labelEl: 'Κακή' },
  { value: 'critical', label: 'Critical', labelEl: 'Κρίσιμη' }
];

// Living conditions options
export const livingConditionsOptions: FormOption[] = [
  { value: 'excellent', label: 'Excellent', labelEl: 'Εξαιρετικές' },
  { value: 'good', label: 'Good', labelEl: 'Καλές' },
  { value: 'adequate', label: 'Adequate', labelEl: 'Επαρκείς' },
  { value: 'poor', label: 'Poor', labelEl: 'Κακές' },
  { value: 'unsafe', label: 'Unsafe', labelEl: 'Επικίνδυνες' }
];

// Relationship quality options
export const relationshipsOptions: FormOption[] = [
  { value: 'excellent', label: 'Excellent', labelEl: 'Εξαιρετικές' },
  { value: 'good', label: 'Good', labelEl: 'Καλές' },
  { value: 'stable', label: 'Stable', labelEl: 'Σταθερές' },
  { value: 'difficult', label: 'Difficult', labelEl: 'Δύσκολες' },
  { value: 'poor', label: 'Poor', labelEl: 'Κακές' },
  { value: 'conflicted', label: 'Conflicted', labelEl: 'Συγκρουσιακές' }
];

// Gender options
export const genderOptions: FormOption[] = [
  { value: 'male', label: 'Male', labelEl: 'Άνδρας' },
  { value: 'female', label: 'Female', labelEl: 'Γυναίκα' },
  { value: 'other', label: 'Other', labelEl: 'Άλλο' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say', labelEl: 'Προτιμώ να μην απαντήσω' }
];

// Country codes for phone numbers (most common countries)
export const countryCodeOptions: FormOption[] = [
  { value: '+30', label: '🇬🇷 +30' },
  { value: '+1', label: '🇺🇸 +1' },
  { value: '+44', label: '🇬🇧 +44' },
  { value: '+49', label: '🇩🇪 +49' },
  { value: '+33', label: '🇫🇷 +33' },
  { value: '+39', label: '🇮🇹 +39' },
  { value: '+34', label: '🇪🇸 +34' },
  { value: '+31', label: '🇳🇱 +31' },
  { value: '+32', label: '🇧🇪 +32' },
  { value: '+41', label: '🇨🇭 +41' },
  { value: '+43', label: '🇦🇹 +43' },
  { value: '+45', label: '🇩🇰 +45' },
  { value: '+46', label: '🇸🇪 +46' },
  { value: '+47', label: '🇳🇴 +47' },
  { value: '+358', label: '🇫🇮 +358' },
  { value: '+48', label: '🇵🇱 +48' },
  { value: '+420', label: '🇨🇿 +420' },
  { value: '+421', label: '🇸🇰 +421' },
  { value: '+36', label: '🇭🇺 +36' },
  { value: '+40', label: '🇷🇴 +40' },
  { value: '+359', label: '🇧🇬 +359' },
  { value: '+385', label: '🇭🇷 +385' },
  { value: '+386', label: '🇸🇮 +386' },
  { value: '+372', label: '🇪🇪 +372' },
  { value: '+371', label: '🇱🇻 +371' },
  { value: '+370', label: '🇱🇹 +370' },
  { value: '+7', label: '🇷🇺 +7' },
  { value: '+380', label: '🇺🇦 +380' },
  { value: '+90', label: '🇹🇷 +90' },
  { value: '+20', label: '🇪🇬 +20' },
  { value: '+966', label: '🇸🇦 +966' },
  { value: '+971', label: '🇦🇪 +971' },
  { value: '+86', label: '🇨🇳 +86' },
  { value: '+81', label: '🇯🇵 +81' },
  { value: '+82', label: '🇰🇷 +82' },
  { value: '+91', label: '🇮🇳 +91' },
  { value: '+61', label: '🇦🇺 +61' },
  { value: '+64', label: '🇳🇿 +64' },
  { value: '+55', label: '🇧🇷 +55' },
  { value: '+54', label: '🇦🇷 +54' },
  { value: '+52', label: '🇲🇽 +52' },
  { value: '+27', label: '🇿🇦 +27' },
  { value: '+234', label: '🇳🇬 +234' },
  { value: '+212', label: '🇲🇦 +212' },
  { value: '+213', label: '🇩🇿 +213' },
  { value: '+216', label: '🇹🇳 +216' },
  { value: '+218', label: '🇱🇾 +218' },
  { value: '+220', label: '🇬🇲 +220' },
  { value: '+221', label: '🇸🇳 +221' },
  { value: '+222', label: '🇲🇷 +222' },
  { value: '+223', label: '🇲🇱 +223' },
  { value: '+224', label: '🇬🇳 +224' },
  { value: '+225', label: '🇨🇮 +225' },
  { value: '+226', label: '🇧🇫 +226' },
  { value: '+227', label: '🇳🇪 +227' },
  { value: '+228', label: '🇹🇬 +228' },
  { value: '+229', label: '🇧🇯 +229' },
  { value: '+230', label: '🇲🇺 +230' },
  { value: '+231', label: '🇱🇷 +231' },
  { value: '+232', label: '🇸🇱 +232' },
  { value: '+233', label: '🇬🇭 +233' },
  { value: '+235', label: '🇹🇩 +235' },
  { value: '+236', label: '🇨🇫 +236' },
  { value: '+237', label: '🇨🇲 +237' },
  { value: '+238', label: '🇨🇻 +238' },
  { value: '+239', label: '🇸🇹 +239' },
  { value: '+240', label: '🇬🇶 +240' },
  { value: '+241', label: '🇬🇦 +241' },
  { value: '+242', label: '🇨🇬 +242' },
  { value: '+243', label: '🇨🇩 +243' },
  { value: '+244', label: '🇦🇴 +244' },
  { value: '+245', label: '🇬🇼 +245' },
  { value: '+246', label: '🇮🇴 +246' },
  { value: '+248', label: '🇸🇨 +248' },
  { value: '+249', label: '🇸🇩 +249' },
  { value: '+250', label: '🇷🇼 +250' },
  { value: '+251', label: '🇪🇹 +251' },
  { value: '+252', label: '🇸🇴 +252' },
  { value: '+253', label: '🇩🇯 +253' },
  { value: '+254', label: '🇰🇪 +254' },
  { value: '+255', label: '🇹🇿 +255' },
  { value: '+256', label: '🇺🇬 +256' },
  { value: '+257', label: '🇧🇮 +257' },
  { value: '+258', label: '🇲🇿 +258' },
  { value: '+260', label: '🇿🇲 +260' },
  { value: '+261', label: '🇲🇬 +261' },
  { value: '+262', label: '🇷🇪 +262' },
  { value: '+263', label: '🇿🇼 +263' },
  { value: '+264', label: '🇳🇦 +264' },
  { value: '+265', label: '🇲🇼 +265' },
  { value: '+266', label: '🇱🇸 +266' },
  { value: '+267', label: '🇧🇼 +267' },
  { value: '+268', label: '🇸🇿 +268' },
  { value: '+269', label: '🇰🇲 +269' },
  { value: '+290', label: '🇸🇭 +290' },
  { value: '+291', label: '🇪🇷 +291' },
  { value: '+297', label: '🇦🇼 +297' },
  { value: '+298', label: '🇫🇴 +298' },
  { value: '+299', label: '🇬🇱 +299' }
];

// Helper function to find option label by value
export const getOptionLabel = (options: FormOption[], value: string, language: 'en' | 'el' = 'en'): string => {
  const option = options.find(opt => opt.value === value);
  return option ? getLocalizedLabel(option, language) : value;
};

// Helper function to get all option values
export const getOptionValues = (options: FormOption[]): string[] => {
  return options.map(option => option.value);
};

// Questionnaire-related types and data
export interface QuestionnaireInfo {
  id: string;
  name: string;
  title: {
    en: string;
    el: string;
  };
  description: {
    en: string;
    el: string;
  };
  instructions: {
    en: string;
    el: string;
  };
  estimatedTime: string;
  questions: number;
}

// Available questionnaires based on backend schemas
export const AVAILABLE_QUESTIONNAIRES: QuestionnaireInfo[] = [
  {
    id: 'aq',
    name: 'Autism Quotient (AQ)',
    title: {
      en: 'Autism Quotient Questionnaire',
      el: 'Ερωτηματολόγιο Αυτιστικού Φάσματος'
    },
    description: {
      en: 'This questionnaire measures traits associated with autism spectrum conditions in adults.',
      el: 'Αυτό το ερωτηματολόγιο μετρά χαρακτηριστικά που σχετίζονται με το αυτιστικό φάσμα σε ενήλικες.'
    },
    instructions: {
      en: 'Please read each statement carefully and choose the response that best describes you.',
      el: 'Παρακαλώ διαβάστε κάθε δήλωση προσεκτικά και επιλέξτε την απάντηση που σας περιγράφει καλύτερα.'
    },
    estimatedTime: '10-15 minutes',
    questions: 50
  },
  {
    id: 'eq',
    name: 'Empathy Quotient (EQ)',
    title: {
      en: 'Empathy Quotient Questionnaire',
      el: 'Ερωτηματολόγιο Συναισθήματος'
    },
    description: {
      en: 'This questionnaire measures your empathy levels and social understanding.',
      el: 'Αυτό το ερωτηματολόγιο μετρά τα επίπεδα συναισθήματος και κοινωνικής κατανόησης.'
    },
    instructions: {
      en: 'Please answer each question honestly based on how you typically feel or behave.',
      el: 'Παρακαλώ απαντήστε σε κάθε ερώτηση ειλικρινά βάσει του πώς συνήθως αισθάνεστε ή συμπεριφέρεστε.'
    },
    estimatedTime: '15-20 minutes',
    questions: 60
  },
  {
    id: 'hamilton',
    name: 'Hamilton Anxiety Scale',
    title: {
      en: 'Hamilton Anxiety Rating Scale',
      el: 'Κλίμακα Άγχους Hamilton'
    },
    description: {
      en: 'This assessment evaluates the severity of anxiety symptoms.',
      el: 'Αυτή η αξιολόγηση εκτιμά τη σοβαρότητα των συμπτωμάτων άγχους.'
    },
    instructions: {
      en: 'Rate the patients by finding the answer which best describes the extent to which he/she has these conditions.',
      el: 'Αξιολογήστε τους ασθενείς βρίσκοντας την απάντηση που περιγράφει καλύτερα τον βαθμό στον οποίο παρουσιάζουν αυτές τις παθήσεις.'
    },
    estimatedTime: '5-10 minutes',
    questions: 14
  },
  {
    id: 'zung',
    name: 'Zung Depression Scale',
    title: {
      en: 'Zung Self-Rating Depression Scale',
      el: 'Κλίμακα Αυτοαξιολόγησης Κατάθλιψης Zung'
    },
    description: {
      en: 'This self-assessment tool evaluates depression symptoms.',
      el: 'Αυτό το εργαλείο αυτοαξιολόγησης εκτιμά τα συμπτώματα κατάθλιψης.'
    },
    instructions: {
      en: 'Please rate how often each statement applies to you.',
      el: 'Παρακαλώ βαθμολογήστε πόσο συχνά ισχύει κάθε δήλωση για εσάς.'
    },
    estimatedTime: '5-10 minutes',
    questions: 20
  },
  {
    id: 'scid',
    name: 'SCID Questionnaire',
    title: {
      en: 'Structured Clinical Interview Questionnaire',
      el: 'Δομημένο Κλινικό Ερωτηματολόγιο'
    },
    description: {
      en: 'This structured interview helps assess various psychological conditions.',
      el: 'Αυτή η δομημένη συνέντευξη βοηθά στην αξιολόγηση διαφόρων ψυχολογικών καταστάσεων.'
    },
    instructions: {
      en: 'Please answer each question as accurately as possible.',
      el: 'Παρακαλώ απαντήστε σε κάθε ερώτηση με όσο το δυνατόν μεγαλύτερη ακρίβεια.'
    },
    estimatedTime: '30-120 minutes',
    questions: 106
  }
];

// Helper functions for questionnaires
export function getQuestionnaireInfo(questionnaireId: string): QuestionnaireInfo | undefined {
  return AVAILABLE_QUESTIONNAIRES.find(q => q.id === questionnaireId);
}

export function isValidQuestionnaireId(questionnaireId: string): boolean {
  return AVAILABLE_QUESTIONNAIRES.some(q => q.id === questionnaireId);
}

export function getAvailableQuestionnaireIds(): string[] {
  return AVAILABLE_QUESTIONNAIRES.map(q => q.id);
}

export function generateQuestionnaireLink(questionnaireId: string, patientId: string): string {
  const baseUrl = window.location.origin;
  // Legacy format - should be replaced with secure token generation
  return `${baseUrl}/questionnaire/${questionnaireId}/${patientId}`;
}


